import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { AppShell } from "@/components/AppShell"
import { BaseBidCard } from "@/components/bids/BaseBidCard"
import { BidStatusBadge } from "@/components/bids/BidStatusBadge"
import { BidSubNav } from "@/components/bids/BidSubNav"
import { MarginCheckCard } from "@/components/bids/MarginCheckCard"
import { MatrixCategorySection } from "@/components/bids/MatrixCategorySection"
import { RoomSelectorView } from "@/components/bids/RoomSelectorView"
import { SyncBanner } from "@/components/bids/SyncBanner"
import { TabStrip } from "@/components/bids/TabStrip"
import { Badge } from "@/components/ui/badge"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { computeSnapshot } from "@/lib/bid-sync"
import { postJSON } from "@/lib/http"
import type { MatrixProps } from "@/types/bids"

type SyncResponse = { ok: boolean; error?: string; details?: Record<string, string> }

export default function Matrix({ community, bid, plans, active_plan }: MatrixProps) {
  const [view, setView] = useState<"matrix" | "selector">("matrix")
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [syncing, setSyncing] = useState(false)
  const [unmeasured, setUnmeasured] = useState<string[]>([])
  const [syncError, setSyncError] = useState<string | null>(null)

  const description = `Plan matrix for ${community.name} — every room at every level, priced per plan.`
  const wire = active_plan?.takeoff_wire ?? null

  const switchPlan = (planId: number) =>
    router.get(bid.urls.matrix, { plan: planId }, { preserveState: true, preserveScroll: true })

  const syncTakeoff = async () => {
    if (!wire || !active_plan) return
    setSyncing(true)
    setSyncError(null)
    const snapshot = computeSnapshot(wire)
    setUnmeasured(snapshot.unmeasured)
    const { status, data } = await postJSON<SyncResponse>(bid.urls.sync, {
      plan_id: active_plan.plan.id,
      plan_version: active_plan.plan.version,
      rows: snapshot.rows,
    })
    setSyncing(false)
    if (data?.ok) {
      router.reload()
    } else if (status === 409) {
      setSyncError("The master takeoff was republished while this page was open — reloading.")
      router.reload()
    } else {
      const detail = data?.details ? ` (${Object.values(data.details).join(" · ")})` : ""
      setSyncError(`Sync failed${detail ? `:${detail}` : " — try again."}`)
    }
  }

  return (
    <>
      <Head title={`${community.name} · Plan matrix`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${community.name} · Plan matrix`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <p className="text-sm text-ink-muted">
          <Link href="/bids" className="hover:text-ink-body">
            Bids
          </Link>{" "}
          / <Link href={bid.urls.workspace} className="hover:text-ink-body">{community.name}</Link>{" "}
          / <strong>Plan matrix</strong>
        </p>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <h1 className="flex items-center gap-3">
            Plan matrix
            <BidStatusBadge status={bid.status} rebidNeeded={bid.rebid_needed} />
          </h1>
          <SegmentedControl
            options={[
              { value: "matrix", label: "Full matrix" },
              { value: "selector", label: "Room selector" },
            ]}
            value={view}
            onChange={(v) => setView(v as "matrix" | "selector")}
          />
        </div>
        <p className="mt-1 text-ink-muted">
          Every room at every level, priced per plan — rates come from the workspace levels; change
          a quoted price there and every cell here reprices.
        </p>

        <BidSubNav
          active="matrix"
          urls={{ workspace: bid.urls.workspace, matrix: bid.urls.matrix, sheet: bid.urls.sheet }}
        />

        {plans.length === 0 ? (
          <div className="callout mt-6 max-w-lg">
            <p>
              No plans in this community yet — add plans to the library and draw their master
              takeoffs first.
            </p>
          </div>
        ) : (
          <>
            <TabStrip className="mt-4">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => switchPlan(plan.id)}
                  className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 pb-2.5 font-medium ${
                    active_plan?.plan.id === plan.id
                      ? "border-accent text-ink-display"
                      : "border-transparent text-ink-muted hover:text-ink-body"
                  }`}
                  data-testid={`matrix-plan-${plan.id}`}
                >
                  {plan.label}
                  {plan.stale && <Badge tone="signal">stale</Badge>}
                  {!plan.synced && !plan.stale && (
                    <span className="font-normal text-ink-muted">· not synced</span>
                  )}
                </button>
              ))}
            </TabStrip>

            {active_plan && (
              <>
                <SyncBanner
                  sync={active_plan.sync}
                  hasWire={wire != null && wire.rooms.length + wire.surfaces.length > 0}
                  published={active_plan.plan.published}
                  busy={syncing}
                  unmeasured={unmeasured}
                  onSync={syncTakeoff}
                />
                {syncError && (
                  <div className="callout-danger mt-2" data-testid="sync-error">
                    <p>{syncError}</p>
                  </div>
                )}

                {active_plan.rows.length === 0 ? (
                  <div className="callout mt-4 max-w-lg">
                    <p>No quantities synced yet for {active_plan.plan.label}.</p>
                  </div>
                ) : view === "selector" ? (
                  <RoomSelectorView
                    key={active_plan.plan.id}
                    planId={active_plan.plan.id}
                    rows={active_plan.rows}
                    categories={active_plan.categories}
                    wasteIn={community.customer.waste_handling === "waste_in"}
                  />
                ) : (
                  <div className="mt-4 space-y-4">
                    {active_plan.categories.length === 0 && (
                      <div className="callout max-w-lg">
                        <p>
                          No priced categories yet — add levels in the{" "}
                          <Link href={bid.urls.workspace}>workspace</Link> first.
                        </p>
                      </div>
                    )}
                    {active_plan.categories.map((category) => (
                      <MatrixCategorySection
                        key={category.key}
                        category={category}
                        rows={active_plan.rows}
                        selections={active_plan.selections}
                        planId={active_plan.plan.id}
                        wasteIn={community.customer.waste_handling === "waste_in"}
                        toggleBaseUrl={bid.urls.toggle_base}
                        collapsed={collapsed.has(category.key)}
                        onToggleCollapsed={() =>
                          setCollapsed((prev) => {
                            const next = new Set(prev)
                            if (next.has(category.key)) next.delete(category.key)
                            else next.add(category.key)
                            return next
                          })
                        }
                      />
                    ))}
                    <div className="grid gap-4 lg:grid-cols-2">
                      <BaseBidCard
                        planLabel={active_plan.plan.label}
                        lines={active_plan.base_bid.lines}
                        total={active_plan.base_bid.total}
                      />
                      <MarginCheckCard check={active_plan.margin_check} />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </AppShell>
    </>
  )
}
